import {createAction} from 'web-app/util/redux/famly-app-helpers';

import type {CreateUpdatePlanPayload, NewPackage, Package, Session} from './types/manage-plan-types';
import type {
    BasePlanActionPayload,
    RemoveBookingActionResult,
    BasePlanPartActionResult,
    RemoveProductBookingActionResult,
    ShowEditActionResult,
    SimpleViewPlanActionPayload,
    SimpleViewPlanActionResult,
    UpdateNoteActionResult,
    UpdateToLaterPriceChangeActionResult,
    ToggleFundableActionResult,
    FetchManagePlanDataActionResult,
    SetBillingProfileActionResult,
    PricingGroupDidChangeActionResult,
    PackagesDidChangeActionResult,
    PackageDidChangeActionResult,
    BaseValidPeriodActionResult,
    DeletePlanActionResult,
    BasePlanActionResult,
    UpdatePlanActionResult,
    UpdateChargeRuleExemptionsActionResult,
} from './types/action-types';

const domain = 'MODALS/MANAGE_PLAN';

export const {action: showCreatePlan} = createAction(
    `${domain}/SHOW_CREATE`,
    (obj: BasePlanActionPayload): BasePlanActionPayload => ({
        institutionId: obj.institutionId,
        behaviors: obj.behaviors,
        childId: obj.childId,
        pricingGroupId: obj.pricingGroupId,
        editPlan: obj.editPlan,
        planUsedSessions: obj.planUsedSessions,
        planUsedProducts: obj.planUsedProducts,
        validOnMustBeBefore: obj.validOnMustBeBefore,
        validOnMustBeAfter: obj.validOnMustBeAfter,
        validToMustBeBefore: obj.validToMustBeBefore,
        validToMustBeAfter: obj.validToMustBeAfter,
        validTo: obj.validTo,
        canEditValidOn: obj.canEditValidOn,
        canEditValidTo: obj.canEditValidTo,
        trackingContext: obj.trackingContext,
        overrideSavePlan: obj.overrideSavePlan,
        overrideSaveText: obj.overrideSaveText,
        templateMode: obj.templateMode,
        onAfterSave: obj.onAfterSave,
    }),
);

export const {action: insertPlan} = createAction(
    `${domain}/INSERT`,
    (obj: BasePlanActionPayload): BasePlanActionPayload => ({
        institutionId: obj.institutionId,
        childId: obj.childId,
        editPlan: obj.editPlan,
        planUsedSessions: obj.planUsedSessions,
        planUsedProducts: obj.planUsedProducts,
        canEditValidOn: obj.canEditValidOn,
        canEditValidTo: obj.canEditValidTo,
        validOnMustBeBefore: obj.validOnMustBeBefore,
        validOnMustBeAfter: obj.validOnMustBeAfter,
        validToMustBeBefore: obj.validToMustBeBefore,
        validToMustBeAfter: obj.validToMustBeAfter,
        trackingContext: 'INSERT_PLAN',
    }),
);

export const {action: showEditPlan} = createAction(
    `${domain}/SHOW_EDIT_PLAN`,
    (obj: BasePlanActionPayload): BasePlanActionPayload => ({
        institutionId: obj.institutionId,
        childId: obj.childId,
        editPlan: obj.editPlan,
        planUsedSessions: obj.planUsedSessions,
        planUsedProducts: obj.planUsedProducts,
        canEditValidOn: obj.canEditValidOn,
        canEditValidTo: obj.canEditValidTo,
        validOnMustBeBefore: obj.validOnMustBeBefore,
        validOnMustBeAfter: obj.validOnMustBeAfter,
        validToMustBeBefore: obj.validToMustBeBefore,
        validToMustBeAfter: obj.validToMustBeAfter,
        trackingContext: 'EDIT_PLAN',
        overrideSavePlan: obj.overrideSavePlan,
        overrideSaveText: obj.overrideSaveText,
        templateMode: obj.templateMode,
        behaviors: obj.behaviors,
        validTo: obj.validTo,
    }),
);

export const {action: simpleViewPlan} = createAction(
    `${domain}/SIMPLE_VIEW_PLAN`,
    (obj: SimpleViewPlanActionPayload): SimpleViewPlanActionResult => ({
        institutionId: obj.institutionId,
        childId: obj.childId,
        editPlan: obj.editPlan,
        planUsedSessions: obj.planUsedSessions,
        planUsedProducts: obj.planUsedProducts,
        isEdit: obj.isEdit,
        overrideSavePlan: obj.overrideSavePlan,
    }),
);

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const {action: closeManagePlan} = createAction(`${domain}/CLOSE`, () => {});

export const {action: updateToLaterPriceChange} = createAction(
    `${domain}/UPDATE_TO_LATER_PRICE_CHANGE`,
    (validFrom: string): UpdateToLaterPriceChangeActionResult => ({
        validFrom,
    }),
);

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const {action: manuallyCloseModal} = createAction(`${domain}/MANUALLY_CLOSE_MODAL`, () => {});

export const {action: showEdit} = createAction(
    `${domain}/SHOW_EDIT`,
    (show: boolean): ShowEditActionResult => ({show}),
);

export const {action: updateNote} = createAction(
    `${domain}/UPDATE_NOTE`,
    (value: string): UpdateNoteActionResult => ({value}),
);

export const {action: addPlanPart} = createAction(`${domain}/ADD_PLAN_PART`, () => ({}));

export const {action: removePlanPart} = createAction(
    `${domain}/REMOVE_PLAN_PART`,
    (planPartId: string): BasePlanPartActionResult => ({planPartId}),
);

export const {action: removeBooking} = createAction(
    `${domain}/REMOVE_BOOKING`,
    (planPartId: string, bookingId: string, session: Session): RemoveBookingActionResult => ({
        planPartId,
        bookingId,
        session,
    }),
);

export const {action: updateChargeRuleExemptions} = createAction(
    `${domain}/UPDATE_CHARGE_RULE_EMPTIONS`,
    (planPartId: string, chargeRuleExemptions: string[]): UpdateChargeRuleExemptionsActionResult => ({
        planPartId,
        chargeRuleExemptions,
    }),
);

export const {action: removeProductBooking} = createAction(
    `${domain}/REMOVE_PRODUCT_BOOKING`,
    (planPartId: string, productBookingId: string, day: string): RemoveProductBookingActionResult => {
        return {
            planPartId,
            productBookingId,
            day,
        };
    },
);

export const {action: removeAllBookings} = createAction(
    `${domain}/REMOVE_ALL_BOOKINGS`,
    (planPartId: string): BasePlanPartActionResult => ({
        planPartId,
    }),
);

export const {action: toggleFundable} = createAction(
    `${domain}/TOGGLE_FUNDING`,
    (planPartId: string, bookingId: string, fundable: boolean | null): ToggleFundableActionResult => ({
        planPartId,
        bookingId,
        fundable,
    }),
);

export const {action: startFetchingData} = createAction(`${domain}/START_FETCHING_DATA`, () => ({}));

export const {
    action: fetchManagePlanData,
    success: fetchManagePlanDataSuccess,
    failed: fetchManagePlanDataFailed,
} = createAction(
    `${domain}/FETCH_DATA`,
    (
        institutionId: string,
        pricingGroupId: string | null,
        validOn: string,
        validTo: string,
        billingProfileIds: string[],
    ): FetchManagePlanDataActionResult => ({
        institutionId,
        pricingGroupId,
        validOn,
        validTo,
        billingProfileIds,
    }),
    (sessions, pricingGroups, pricingGroupBehaviors, billingProfiles, attendanceSchedules) => ({
        sessions,
        pricingGroups,
        pricingGroupBehaviors,
        billingProfiles,
        attendanceSchedules,
    }),
);

export const {
    action: fetchRuleGroups,
    success: fetchRuleGroupsSuccess,
    failed: fetchRuleGroupsFailed,
} = createAction(
    `${domain}/FETCH_RULE_GROUPS`,
    () => ({}),
    ruleGroupOptions => {
        return {
            ruleGroupOptions,
        };
    },
);

export const {
    action: supportsRuleSets,
    success: supportsRuleSetsSuccess,
    failed: supportsRuleSetsFailed,
} = createAction(
    `${domain}/SUPPORTS_RULE_SETS`,
    () => ({}),
    supportsRuleSets => {
        return {
            supportsRuleSets,
        };
    },
);

export const {
    action: isPlanRulesEnabled,
    success: isPlanRulesEnabledSuccess,
    failed: isPlanRulesEnabledFailed,
} = createAction(
    `${domain}/IS_PLAN_RULES_ENABLED`,
    () => ({}),
    isPlanRulesEnabled => {
        return {
            isPlanRulesEnabled,
        };
    },
);

export const {
    action: isAutomaticChargesEnabled,
    success: isAutomaticChargesEnabledSuccess,
    failed: isAutomaticChargesEnabledFailed,
} = createAction(
    `${domain}/IS_AUTOMATIC_CHARGES_ENABLED`,
    () => ({}),
    isAutomaticChargesEnabled => {
        return {
            isAutomaticChargesEnabled,
        };
    },
);

export const {
    action: hasPreventDoubleBookingEnabled,
    success: hasPreventDoubleBookingEnabledSuccess,
    failed: hasPreventDoubleBookingEnabledFailed,
} = createAction(
    `${domain}/HAS_PREVENT_DOUBLE_BOOKING_ENABLED`,
    () => ({}),
    hasPreventDoubleBookingEnabled => {
        return {
            hasPreventDoubleBookingEnabled,
        };
    },
);

export const {action: startPreviewPlan} = createAction(`${domain}/START_PREVIEW`, () => ({}));

export const {
    action: previewPlan,
    success: previewPlanSuccess,
    failed: previewPlanFailed,
    aborted: previewPlanAborted,
} = createAction(
    `${domain}/PREVIEW`,
    () => ({}),
    // todo: find the right type for plan param
    (
        plan,
        isPlanRulesEnabled: boolean = false,
        hasPreventDoubleBookingEnabled: boolean = false,
        isAutomaticChargesEnabled: boolean = false,
    ) => ({
        plan,
        isPlanRulesEnabled,
        hasPreventDoubleBookingEnabled,
        isAutomaticChargesEnabled,
    }),
);

export const {
    action: previewPlanPublicFunding,
    success: previewPlanPublicFundingSuccess,
    failed: previewPlanPublicFundingFailed,
    aborted: previewPlanPublicFundingAborted,
} = createAction(
    `${domain}/PREVIEW_PLAN_PUBLIC_FUNDING`,
    () => ({}),
    plan => ({plan}),
);

export const {
    action: createPlan,
    success: createPlanSuccess,
    failed: createPlanFailed,
} = createAction(
    `${domain}/CREATE_PLAN`,
    (plan: CreateUpdatePlanPayload, institutionId: string): BasePlanActionResult => ({plan, institutionId}),
    (plan: CreateUpdatePlanPayload, institutionId: string): BasePlanActionResult => ({plan, institutionId}),
);

export const {
    action: updatePlan,
    success: updatePlanSuccess,
    failed: updatePlanFailed,
} = createAction(
    `${domain}/UPDATE_PLAN`,
    (plan: CreateUpdatePlanPayload, institutionId: string, oldPlanId: string): UpdatePlanActionResult => ({
        plan,
        institutionId,
        oldPlanId,
    }),
    (plan: CreateUpdatePlanPayload, institutionId: string): BasePlanActionResult => ({plan, institutionId}),
);

export const {
    action: deletePlan,
    success: deletePlanSuccess,
    failed: deletePlanFailed,
    aborted: deletePlanAborted,
} = createAction(
    `${domain}/DELETE`,
    (planId: string, childId: string, institutionId: string): DeletePlanActionResult => ({
        planId,
        childId,
        institutionId,
    }),
);

export const {action: planPeriodDidChange} = createAction(
    `${domain}/PERIOD_CHANGE`,
    (validOn: string, validTo: string): BaseValidPeriodActionResult => ({
        validOn,
        validTo,
    }),
);

export const {action: packageDidChange} = createAction(
    `${domain}/PACKAGE_CHANGE`,
    (
        planPartId: string,
        planPackage: NewPackage | null,
        session: Session | null,
        oldPackage: Package | null,
    ): PackageDidChangeActionResult => ({planPartId, planPackage, session, oldPackage}),
);

export const {action: packagesDidChange} = createAction(
    `${domain}/PACKAGES_CHANGE`,
    (planPartId: string, packages: Package[]): PackagesDidChangeActionResult => ({
        planPartId,
        packages,
    }),
);

export const {action: pricingGroupDidChange} = createAction(
    `${domain}/PRICING_GROUP_CHANGE`,
    (pricingGroupId: string, pricingGroupType: string): PricingGroupDidChangeActionResult => ({
        pricingGroupType,
        pricingGroupId,
    }),
);

export const {action: setBillingProfile} = createAction(
    `${domain}/SET_BILLING_PROFILE`,
    // todo: find type for billingProfile
    (planPartId: string, billingProfile: any | null, override?: boolean): SetBillingProfileActionResult => ({
        planPartId,
        billingProfile,
        override,
    }),
);

export const {action: openBillingProfileModal} = createAction(
    `${domain}/OPEN_BILLING_PROFILE_MODAL`,
    (planPartId: string): BasePlanPartActionResult => ({
        planPartId,
    }),
);

export const {action: closeBillingProfileModal} = createAction(`${domain}/CLOSE_BILLING_PROFILE_MODAL`, () => ({}));
